import { doc, getDoc, updateDoc, setDoc, collection, onSnapshot } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { Firm, FirmSettings, FirmSubscription } from '@/types';
import { getCurrentTimestamp } from '@/utils/dates';
import { removeUndefinedFields } from '@/utils/objectHelpers';
import { withErrorHandling } from '@/utils/errorHandling';
import { logger } from '@/utils/logger';
import { DEFAULT_FIRM_SIGNATURE } from '@/utils/constants';

/**
 * Create a new firm
 */
export async function createFirm(
  firmName: string,
  ownerEmail: string
): Promise<string | undefined> {
  return withErrorHandling(async () => {
    // Generate a new firm document reference with auto-generated ID
    const firmRef = doc(collection(db, 'firms'));
    const firmId = firmRef.id;

    // Default firm settings. Request-email copy is NOT stored here — it lives in
    // the firm's seeded emailTemplates collection (SYSTEM_TEMPLATE_DEFINITIONS in
    // emailTemplateService.ts). settings.emailTemplate carries only the firm-wide
    // signature, edited in Settings → Firm Info.
    const defaultSettings: FirmSettings = {
      defaultRequestInterval: 30,
      ccParalegalEnabled: true,
      emailTemplate: {
        signature: DEFAULT_FIRM_SIGNATURE,
      },
      firmEmail: ownerEmail,
      replyToEmail: ownerEmail,
      firmPhone: '',
      firmAddress: '',
    };


    // Default subscription (inactive until Stripe setup)
    const defaultSubscription: FirmSubscription = {
      status: 'inactive',
      tier: 'standard',
      billingEmail: ownerEmail,
      stripeCustomerId: '',
      stripeSubscriptionId: '',
      currentPeriodEnd: getCurrentTimestamp(),
      activeCasesCount: 0,
    };

    // Create firm document
    const newFirm: Firm = {
      firmId,
      firmName,
      ownerEmail,
      createdAt: getCurrentTimestamp(),
      updatedAt: getCurrentTimestamp(),
      subscription: defaultSubscription,
      settings: defaultSettings,
      auditLog: true,
      dataRetentionDays: 2555, // ~7 years for HIPAA compliance
    };

    await setDoc(firmRef, newFirm);

    // Step 5: Seed system email templates for the firm
    await withErrorHandling(async () => {
      const { seedSystemTemplates } = await import('./emailTemplateService');
      // Use firmName as the creator name for system templates
      await seedSystemTemplates(firmId, 'system', firmName);
    }, 'seeding email templates during firm setup', { rethrow: false });

    return firmId;
  }, 'creating firm');
}

/**
 * Get firm details
 */
export async function getFirm(firmId: string): Promise<Firm | null | undefined> {
  return withErrorHandling(async () => {
    const firmRef = doc(db, 'firms', firmId);
    const firmDoc = await getDoc(firmRef);

    if (firmDoc.exists()) {
      return firmDoc.data() as Firm;
    }

    return null;
  }, 'fetching firm details');
}

/**
 * Subscribe to firm details in real-time
 */
export function subscribeToFirm(
  firmId: string,
  onUpdate: (firm: Firm | null) => void,
  onError?: (error: Error) => void
): () => void {
  const firmRef = doc(db, 'firms', firmId);

  return onSnapshot(
    firmRef,
    (snapshot) => {
      if (snapshot.exists()) {
        onUpdate(snapshot.data() as Firm);
      } else {
        onUpdate(null);
      }
    },
    (error) => {
      logger.error('Error subscribing to firm details:', error);
      if (onError) onError(error);
    }
  );
}

/**
 * Update firm settings
 * Uses dot notation to properly merge nested fields without replacing the entire settings object
 */
export async function updateFirmSettings(
  firmId: string,
  settings: Partial<FirmSettings>
): Promise<void | undefined> {
  return withErrorHandling(async () => {
    const firmRef = doc(db, 'firms', firmId);

    // Remove undefined values before updating Firestore
    const cleanedSettings = removeUndefinedFields(settings);

    // Convert to dot notation for proper Firestore merge
    // e.g., { emailTemplate: { body: "..." } } becomes { "settings.emailTemplate": { body: "..." } }
    const updateData: Record<string, unknown> = {
      updatedAt: getCurrentTimestamp(),
    };

    // Flatten the settings object with "settings." prefix
    for (const [key, value] of Object.entries(cleanedSettings)) {
      updateData[`settings.${key}`] = value;
    }

    await updateDoc(firmRef, updateData);
  }, 'updating firm settings');
}

/**
 * Update firm information
 */
export async function updateFirmInfo(
  firmId: string,
  data: {
    firmName?: string;
    ownerEmail?: string;
  }
): Promise<void | undefined> {
  return withErrorHandling(async () => {
    const firmRef = doc(db, 'firms', firmId);
    const updateData: Record<string, unknown> = {
      updatedAt: getCurrentTimestamp(),
    };

    if (data.firmName !== undefined) {
      updateData.firmName = data.firmName;
    }

    if (data.ownerEmail !== undefined) {
      updateData.ownerEmail = data.ownerEmail;
    }

    await updateDoc(firmRef, updateData);
  }, 'updating firm information');
}

/**
 * Mark the "invite team" onboarding step as skipped (solo firms).
 * Sets settings.inviteTeamSkippedAt; the dashboard widget treats a non-null
 * timestamp as a manual override that completes the step. Inviting someone
 * later still completes the step naturally via activeUsers/pendingInvitations.
 */
export async function markInviteTeamSkipped(
  firmId: string,
): Promise<void | undefined> {
  return updateFirmSettings(firmId, {
    inviteTeamSkippedAt: getCurrentTimestamp(),
  });
}
